import React, { useMemo, useState } from "react";

function normalize(s) {
	return (s || "").trim().toLowerCase();
}

function templateInfo(t) {
	const id = t.Id ?? t.id;
	const nom = t.Nom ?? t.nom ?? "Sans nom";
	const exos = t.ListExercice ?? t.listExercice ?? t.exercices ?? [];
	const seriesTotal = exos.reduce((sum, ex) => {
		if (typeof ex === "string") return sum;
		const s = ex.ListSerie ?? ex.listSerie ?? ex.series ?? [];
		return sum + (Array.isArray(s) ? s.length : 0);
	}, 0);
	return { id, nom, exos, seriesTotal };
}

function exoNames(exos, max = 8) {
	return exos.slice(0, max).map((ex) =>
		typeof ex === "string" ? ex : (ex.NomExercice ?? ex.nomExercice ?? ex.nom ?? "")
	);
}

export default function TemplatesPanel({
	studentTemplates,
	coachTemplates,
	templateFolders = [],
	templateFolderAssoc = [],
	studentName,
	loading,
	onOpenTemplate,           // libraryMode : ouvrir pour édition ; sinon = fallback ancien
	onOpenAsSession,          // charger le template comme nouvelle séance (mode élève)
	onEditAsTemplate,         // ouvrir le template pour l'éditer (bascule en mode template)
	onDeleteTemplate,         // supprimer le template
	onImportTemplate,
	// Handlers dossiers élève (fournis uniquement en mode élève, undefined en libraryMode)
	onCreateFolder,           // (name) => void — création dossier
	onDeleteFolder,           // (folderKey) => void — suppression dossier
	onAssignFolder,           // (folderKey, templateKey, assign: bool) => void — assign/unassign template
	libraryMode = false,
}) {
	const [mode, setMode] = useState("list"); // "list" | "import"
	const [importQuery, setImportQuery] = useState("");
	const [importingId, setImportingId] = useState(null);
	const [activeFolderKey, setActiveFolderKey] = useState(null);
	// Création dossier inline
	const [showNewFolderInput, setShowNewFolderInput] = useState(false);
	const [newFolderName, setNewFolderName] = useState("");
	// Modale d'assignation dossiers pour un template
	const [assignTargetTpl, setAssignTargetTpl] = useState(null);

	const folders = useMemo(
		() => (templateFolders || []).filter((f) => !(f.Supprime ?? f.supprime)),
		[templateFolders]
	);

	const folderCount = (folderKey) =>
		(templateFolderAssoc || []).filter((a) => {
			const aSupprime = a.Supprime ?? a.supprime;
			const aFolderKey = a.FolderKey ?? a.folderKey;
			return !aSupprime && aFolderKey === folderKey;
		}).length;

	const importList = useMemo(() => {
		const q = normalize(importQuery);
		const studentNamesSet = new Set((studentTemplates || []).map((t) => normalize(t.nom ?? t.Nom)));
		return (coachTemplates || [])
			.filter((t) => {
				const nom = t.Nom ?? t.nom ?? "";
				if (q && !normalize(nom).includes(q)) return false;
				if (activeFolderKey !== null) {
					const tKey = t.Key ?? t.key;
					const inFolder = (templateFolderAssoc || []).some((a) => {
						const aSupprime = a.Supprime ?? a.supprime;
						const aFolderKey = a.FolderKey ?? a.folderKey;
						const aTemplateKey = a.TemplateKey ?? a.templateKey;
						return !aSupprime && aFolderKey === activeFolderKey && aTemplateKey === tKey;
					});
					if (!inFolder) return false;
				}
				return true;
			})
			.map((t) => ({ ...t, _inStudent: studentNamesSet.has(normalize(t.Nom ?? t.nom)) }));
	}, [coachTemplates, importQuery, studentTemplates, activeFolderKey, templateFolderAssoc]);

	const handleImport = async (t) => {
		if (!onImportTemplate) return;
		const id = t.Id ?? t.id;
		setImportingId(id);
		await onImportTemplate(t);
		setImportingId(null);
	};

	// ===== Import sub-view =====
	if (mode === "import") {
		return (
			<div className="editor-panel-templates">
				<button className="editor-panel-exercises-back" onClick={() => { setMode("list"); setImportQuery(""); }}>
					<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
						<line x1="19" y1="12" x2="5" y2="12" />
						<polyline points="12 19 5 12 12 5" />
					</svg>
					Mes templates
				</button>

				<div className="editor-panel-exercises-subtitle">
					Importer dans la bibliothèque de {studentName || "l'élève"}
				</div>

				<div className="editor-panel-exercises-search">
					<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
						<circle cx="11" cy="11" r="8" />
						<line x1="21" y1="21" x2="16.65" y2="16.65" />
					</svg>
					<input
						type="text"
						value={importQuery}
						onChange={(e) => setImportQuery(e.target.value)}
						placeholder="Rechercher dans mes templates"
						autoFocus
					/>
					{importQuery && (
						<button className="editor-panel-exercises-clear" onClick={() => setImportQuery("")}>×</button>
					)}
				</div>

				{folders.length > 0 && (
					<div className="editor-panel-templates-folderpills">
						<button
							className={`editor-panel-templates-folderpill ${activeFolderKey === null ? "active" : ""}`}
							onClick={() => setActiveFolderKey(null)}
						>
							Tous <span className="cnt">{(coachTemplates || []).length}</span>
						</button>
						{folders.map((f) => {
							const fKey = f.Key ?? f.key;
							const fName = f.NomDossier ?? f.nomDossier ?? "Dossier";
							return (
								<button
									key={fKey}
									className={`editor-panel-templates-folderpill ${activeFolderKey === fKey ? "active" : ""}`}
									onClick={() => setActiveFolderKey(fKey)}
								>
									{fName} <span className="cnt">{folderCount(fKey)}</span>
								</button>
							);
						})}
					</div>
				)}

				<div className="editor-panel-templates-list">
					{importList.length === 0 ? (
						<div className="editor-panel-templates-empty">Aucun template dans tes modèles</div>
					) : (
						importList.map((t) => {
							const { id, nom, exos, seriesTotal } = templateInfo(t);
							return (
								<button
									key={id}
									className={`editor-panel-templates-card ${t._inStudent ? "is-disabled" : ""}`}
									onClick={() => !t._inStudent && handleImport(t)}
									disabled={t._inStudent || importingId === id}
								>
									<div className="editor-panel-templates-card-name">{nom}</div>
									<div className="editor-panel-templates-card-stats">
										{exos.length} exo{exos.length > 1 ? "s" : ""}
										{seriesTotal > 0 && ` · ${seriesTotal} sér.`}
									</div>
									<div className="editor-panel-templates-card-cta">
										{t._inStudent ? "Déjà dans sa biblio" : importingId === id ? "Import…" : "Importer →"}
									</div>
								</button>
							);
						})
					)}
				</div>
			</div>
		);
	}

	// ===== Main list view =====
	const rawMainList = libraryMode ? coachTemplates : studentTemplates;
	// Filtre par dossier actif (student mode uniquement pour l'instant)
	const mainList = (!libraryMode && activeFolderKey != null)
		? (rawMainList || []).filter((t) => {
			const tKey = t.Key ?? t.key ?? t.Id ?? t.id;
			return (templateFolderAssoc || []).some((a) => {
				const aSupprime = a.Supprime ?? a.supprime;
				const aFolderKey = a.FolderKey ?? a.folderKey;
				const aTemplateKey = a.TemplateKey ?? a.templateKey;
				return !aSupprime && aFolderKey === activeFolderKey && aTemplateKey === tKey;
			});
		})
		: rawMainList;

	const handleCreateFolderSubmit = () => {
		const name = newFolderName.trim();
		if (!name || !onCreateFolder) return;
		onCreateFolder(name);
		setNewFolderName("");
		setShowNewFolderInput(false);
	};

	const handleDeleteFolderClick = (folderKey, folderName) => {
		if (!onDeleteFolder) return;
		if (!window.confirm(`Supprimer le dossier "${folderName}" ? Les templates ne seront pas supprimés.`)) return;
		if (activeFolderKey === folderKey) setActiveFolderKey(null);
		onDeleteFolder(folderKey);
	};

	return (
		<div className="editor-panel-templates">
			<div className="editor-panel-exercises-owner">
				{libraryMode ? <strong>Mes templates</strong> : <>Templates de <strong>{studentName || "l'élève"}</strong></>}
			</div>

			{!libraryMode && (
				<div className="editor-panel-templates-importbar top">
					<button className="editor-panel-exercises-new-btn" onClick={() => setMode("import")}>
						<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6, verticalAlign: "-2px" }}>
							<path d="M20 16.58A5 5 0 0 0 18 7h-1.26A8 8 0 1 0 4 15.25" />
							<polyline points="8 17 12 21 16 17" />
							<line x1="12" y1="12" x2="12" y2="21" />
						</svg>
						Template Coach
					</button>
				</div>
			)}

			{/* Pills de dossiers (mode élève avec onCreateFolder) */}
			{!libraryMode && onCreateFolder && (
				<div className="editor-panel-templates-folderpills" style={{ marginBottom: 10 }}>
					<button
						className={`editor-panel-templates-folderpill ${activeFolderKey === null ? "active" : ""}`}
						onClick={() => setActiveFolderKey(null)}
					>
						Tous <span className="cnt">{(rawMainList || []).length}</span>
					</button>
					{folders.map((f) => {
						const fKey = f.Key ?? f.key;
						const fName = f.NomDossier ?? f.nomDossier ?? "Dossier";
						return (
							<span key={fKey} className={`editor-panel-templates-folderpill ${activeFolderKey === fKey ? "active" : ""}`} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
								<button
									onClick={() => setActiveFolderKey(fKey)}
									style={{ background: "transparent", border: 0, padding: 0, color: "inherit", cursor: "pointer" }}
								>
									{fName} <span className="cnt">{folderCount(fKey)}</span>
								</button>
								{onDeleteFolder && (
									<button
										onClick={(e) => { e.stopPropagation(); handleDeleteFolderClick(fKey, fName); }}
										title="Supprimer le dossier"
										style={{ background: "transparent", border: 0, padding: 0, marginLeft: 2, color: "inherit", opacity: 0.55, cursor: "pointer", fontSize: 14, lineHeight: 1 }}
									>
										×
									</button>
								)}
							</span>
						);
					})}
					{showNewFolderInput ? (
						<span className="editor-panel-templates-folderpill" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
							<input
								autoFocus
								value={newFolderName}
								onChange={(e) => setNewFolderName(e.target.value)}
								onKeyDown={(e) => {
									if (e.key === "Enter") handleCreateFolderSubmit();
									else if (e.key === "Escape") { setNewFolderName(""); setShowNewFolderInput(false); }
								}}
								placeholder="Nom du dossier"
								style={{ background: "transparent", border: 0, outline: 0, color: "inherit", width: 120, fontSize: 12 }}
							/>
							<button
								onClick={handleCreateFolderSubmit}
								disabled={!newFolderName.trim()}
								style={{ background: "transparent", border: 0, color: "inherit", cursor: "pointer", opacity: newFolderName.trim() ? 1 : 0.4 }}
							>
								✓
							</button>
						</span>
					) : (
						<button
							className="editor-panel-templates-folderpill"
							onClick={() => setShowNewFolderInput(true)}
							title="Créer un dossier"
						>
							+ Dossier
						</button>
					)}
				</div>
			)}

			<div className="editor-panel-templates-list">
				{loading ? (
					<div className="editor-panel-templates-empty">Chargement…</div>
				) : !mainList || mainList.length === 0 ? (
					<div className="editor-panel-templates-empty">
						{libraryMode ? "Aucun template dans ta bibliothèque" : (activeFolderKey != null ? "Aucun template dans ce dossier" : "Aucun template pour cet élève")}
					</div>
				) : (
					mainList.map((t) => {
						const { id, nom, exos, seriesTotal } = templateInfo(t);
						const names = exoNames(exos);
						// Card cliquable : ouvre directement le template en mode edition
						// (bascule libraryMode via onEditAsTemplate en mode eleve,
						// ou charge dans l'editeur template via onOpenTemplate en libraryMode).
						const handleCardClick = () => {
							if (libraryMode) onOpenTemplate?.(t);
							else onEditAsTemplate?.(t);
						};
						return (
							<button
								type="button"
								key={id}
								className="editor-panel-templates-card"
								onClick={handleCardClick}
							>
								<div className="editor-panel-templates-card-head">
									<div className="editor-panel-templates-card-name">{nom}</div>
									<div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
										{!libraryMode && onAssignFolder && folders.length > 0 && (
											<button
												type="button"
												className="editor-panel-templates-card-delete"
												onClick={(e) => { e.stopPropagation(); setAssignTargetTpl(t); }}
												title="Ranger dans un dossier"
												aria-label="Ranger dans un dossier"
												style={{ borderColor: "var(--line-soft)", color: "var(--ink-soft)" }}
											>
												<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
													<path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
												</svg>
											</button>
										)}
										{onDeleteTemplate && (
											<button
												type="button"
												className="editor-panel-templates-card-delete"
												onClick={(e) => { e.stopPropagation(); onDeleteTemplate(t); }}
												title="Supprimer le template"
												aria-label="Supprimer le template"
											>
												<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
													<polyline points="3 6 5 6 21 6" />
													<path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
													<path d="M10 11v6" />
													<path d="M14 11v6" />
													<path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
												</svg>
											</button>
										)}
									</div>
								</div>
								<div className="editor-panel-templates-card-stats">
									{exos.length} exo{exos.length > 1 ? "s" : ""}
									{seriesTotal > 0 && ` · ${seriesTotal} sér.`}
								</div>
								{names.length > 0 && (
									<div className="editor-panel-templates-card-exos">
										{names.reduce((acc, n, i) => i === 0 ? [<span key={i}>{n}</span>] : [...acc, " · ", <span key={i}>{n}</span>], [])}
										{exos.length > names.length && <span> +{exos.length - names.length}</span>}
									</div>
								)}
							</button>
						);
					})
				)}
			</div>

			{/* Modale d'assignation dossiers pour un template (élève uniquement) */}
			{assignTargetTpl && onAssignFolder && (
				<div className="mens-modal-overlay" onClick={() => setAssignTargetTpl(null)}>
					<div className="mens-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420 }}>
						<div className="mens-modal-header">
							<h3>Ranger « {assignTargetTpl.Nom ?? assignTargetTpl.nom} »</h3>
							<button className="mens-modal-close" onClick={() => setAssignTargetTpl(null)}>×</button>
						</div>
						{folders.length === 0 ? (
							<p style={{ color: "var(--ink-soft)", fontSize: 13, marginTop: 8 }}>
								Aucun dossier disponible. Crée-en un depuis la barre en haut.
							</p>
						) : (
							<div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 10, maxHeight: 320, overflowY: "auto" }}>
								{folders.map((f) => {
									const fKey = f.Key ?? f.key;
									const fName = f.NomDossier ?? f.nomDossier ?? "Dossier";
									const tKey = assignTargetTpl.Key ?? assignTargetTpl.key ?? assignTargetTpl.Id ?? assignTargetTpl.id;
									const isIn = (templateFolderAssoc || []).some((a) => {
										const aSupprime = a.Supprime ?? a.supprime;
										const aFolderKey = a.FolderKey ?? a.folderKey;
										const aTemplateKey = a.TemplateKey ?? a.templateKey;
										return !aSupprime && aFolderKey === fKey && aTemplateKey === tKey;
									});
									return (
										<button
											key={fKey}
											type="button"
											onClick={() => onAssignFolder(fKey, tKey, !isIn)}
											style={{
												display: "flex", alignItems: "center", justifyContent: "space-between",
												padding: "10px 12px", borderRadius: 8, border: "1px solid var(--line-soft)",
												background: isIn ? "rgba(48,164,154,0.10)" : "transparent",
												color: "var(--ink)", cursor: "pointer", fontSize: 13,
											}}
										>
											<span>{fName}</span>
											<span style={{ fontSize: 12, color: isIn ? "#30a49a" : "var(--ink-soft)", fontWeight: 700 }}>
												{isIn ? "✓ Rangé" : "Ranger"}
											</span>
										</button>
									);
								})}
							</div>
						)}
						<div className="mens-modal-actions">
							<button className="mens-btn primary" onClick={() => setAssignTargetTpl(null)}>Fermer</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
